import { List, Button, Tag, Modal, Spin, Tooltip } from "antd";
import { useState } from "react";
import { EditFilled, DeleteFilled, LockFilled, UnlockFilled } from "@ant-design/icons";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { StatutService } from "../../services/StatutService";
import StatutEditForm from "./StatutEditForm";
import PropTypes from "prop-types";
import styles from "./StatutList.module.scss";
import ReorganisableList from "../utils/ReorganisableList";

const StatutList = ({ statuts, setStatuts, reorder, setReorder }) => {
  const [loading, setLoading] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedStatut, setSelectedStatut] = useState(null);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);

  const handleDelete = (idOrUid) => {
    setLoading(true);
    StatutService.getInstance()
      .remove(idOrUid)
      .then((response) => {
        Toast.success(response.message);
        setStatuts((items) => items.filter((item) => item.uid !== idOrUid));
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  };

  const openEditModal = (item) => {
    setSelectedStatut(item);
    setUpdateModalVisible(true);
  };

  const handleEnable = (statut) => {
    StatutService.getInstance()
      .enable(statut.uid)
      .then((response) => {
        Toast.success(response.message);
        setStatuts((prevStatuts) =>
          prevStatuts.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleDisable = (statut) => {
    StatutService.getInstance()
      .disable(statut.uid)
      .then((response) => {
        Toast.success(response.message);
        setStatuts((prevStatuts) =>
          prevStatuts.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleUpdate = (item) => {
    setStatuts((prevStatuts) => prevStatuts.map((u) => (u.uid === item.uid ? item : u)));
    setSelectedStatut(null);
    setUpdateModalVisible(false);
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      width: 150,
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 150,
    },
    {
      title: "Statut",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) => (
        enabled ? <Tag color="success">Actif</Tag> : <Tag color="error">Inactif</Tag>
      ),
      width: 150,
    },

  ];

  const updateStatutOrder = (newOrderData) => {
    setIsOrderUpdating(true);
    StatutService.getInstance()
      .updateOrder(newOrderData.map(item => item.id))
      .then(() => {
        Toast.success("L'ordre a été mis à jour avec succès");
        setReorder(false);
      })
      .catch(catchError)
      .finally(() => setIsOrderUpdating(false));
  }

  return (
    <>
      <div className={styles.statutListHeaderDescription}>
        <p>Les statuts permettent de définir l&apos;état d&apos;une tâche.</p>
        <p>L&apos;ordre des statuts est important pour la gestion des tâches car il définit le cycle de vie d&apos;une tâche.</p>
        <p>Les statuts peuvent être activés ou désactivés. Ce qui permet de les masquer ou les afficher dans les listes.</p>
      </div>

      <Spin spinning={loading}>
        {reorder ? (<ReorganisableList initialData={statuts} initialColumns={columns} updatingOrder={isOrderUpdating} onSaveOrder={updateStatutOrder} onCancelOrder={() => setReorder(false)} />) :
          (<List
            className={styles.statutList}
            itemLayout="horizontal"
            dataSource={statuts}
            renderItem={(item) => (
              <List.Item
                className={styles.listItem}
                actions={[
                  <Tooltip title="Modifier" key="edit">
                    <Button
                      icon={<EditFilled />}
                      onClick={() => openEditModal(item)}
                      type="text"
                    />
                  </Tooltip>,
                  <Tooltip title={item.enabled ? "Désactiver" : "Activer"} key="toggle">
                    <Button
                      icon={item.enabled ? <LockFilled /> : <UnlockFilled />}
                      onClick={() => {
                        Modal.confirm({
                          title: `Voulez-vous ${item.enabled ? "désactiver" : "activer"} ce statut?`,
                          okText: item.enabled ? "Désactiver" : "Activer",
                          cancelText: "Annuler",
                          okType: item.enabled ? "danger" : "primary",
                          onOk: () => (item.enabled ? handleDisable(item) : handleEnable(item)),
                        });
                      }}
                      type="text"
                    />
                  </Tooltip>,
                  <Tooltip title="Supprimer" key="delete">
                    <Button
                      icon={<DeleteFilled />}
                      onClick={() =>
                        Modal.confirm({
                          title: "Voulez-vous vraiment supprimer ce statut ?",
                          okText: "Confirmer",
                          cancelText: "Annuler",
                          okType: "danger",
                          onOk: () => handleDelete(item.uid),
                        })
                      }
                      type="text"
                      danger
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  title={<span className={styles.statutTitle}>{item.nom}</span>}
                  description={
                    <div className={styles.metaDescription}>
                      <Tag color={item.enabled ? "success" : "error"}>
                        {item.enabled ? "Actif" : "Inactif"}
                      </Tag>
                      <Tag className={styles.colorTag} style={{ backgroundColor: item.couleur }}>
                        {item.couleur}
                      </Tag>
                      <Tag color={item.resolu ? "success" : "default"}>{item.resolu ? "Résolu" : "Non résulu"}</Tag>
                    </div>
                  }
                />
              </List.Item>
            )}
          />)}
      </Spin>

      {selectedStatut && (
        <StatutEditForm
          visible={updateModalVisible}
          uid={selectedStatut.uid}
          onUpdate={handleUpdate}
          onCancel={() => setUpdateModalVisible(false)}
        />
      )}
    </>
  );
};

export default StatutList;

StatutList.propTypes = {
  searchParams: PropTypes.object,
  statuts: PropTypes.array.isRequired,
  setStatuts: PropTypes.func.isRequired,
  reorder: PropTypes.bool,
  setReorder: PropTypes.func,
};
